import { SuggestedPriceOverrideAttribution } from '@features/suggested-price-column/types/override-attribution';
import { parseWhodunnit } from '@utils/whodunnit';

/** The two already-display-ready values the attribution sentence interpolates. */
export interface ResolvedOverrideAttribution {
  date: string;
  name: string;
}

/**
 * Resolves a Change Log record's raw `whodunnit` / `created_at` pair into the attribution
 * sentence's values, or `null` when the pair cannot produce a whole sentence.
 *
 * Both fields are required together: a name with no timestamp — or the reverse — renders a
 * half-empty sentence, and an unattributed override is a normal state rather than a fault, so the
 * caller omits the sentence entirely instead of filling the gap with a placeholder.
 *
 * Name resolution mirrors the Change Log's own column: a structured whodunnit yields its name,
 * falling back to the email for users with no first name, and an unstructured value (a `system` or
 * `intelligent_counter_offer` sentinel) is passed through as-is.
 *
 * `formatDate` is injected rather than imported so this stays independent of the tenant's
 * date-part-order context and testable without it.
 */
export const resolveOverrideAttribution = (
  { createdAt, whodunnit }: SuggestedPriceOverrideAttribution,
  formatDate: (isoTimestamp: string) => string,
): ResolvedOverrideAttribution | null => {
  if (!createdAt || !whodunnit) return null;

  const parsed = parseWhodunnit(whodunnit);
  const name = parsed ? (parsed.name || parsed.email) : whodunnit;
  if (!name) return null;

  const date = formatDate(createdAt);
  if (!date) return null;

  return { date, name };
};
